/**
 * Redirect URL Utility
 *
 * Simple localStorage-based utility for persisting redirect URLs across
 * page reloads during auto-logout scenarios.
 *
 * Flow:
 * 1. User visits a share link (e.g., /themes/123/images#overview)
 * 2. Session expires → Auth.logout('preserve-redirect') saves current URL
 * 3. User is redirected to /login
 * 4. Login page reads stored URL on successful login
 * 5. User is redirected back to their original destination
 */

export const REDIRECT_URL_STORAGE_KEY = "adora-post-login-redirect";

/**
 * Validates that a URL is safe for internal redirect.
 *
 * Security rules:
 * - Must start with '/' (internal path)
 * - Cannot start with '//' (protocol-relative URLs like //evil.com)
 * - Cannot contain ':' before first '/' (prevents javascript:, data:, http:, etc.)
 * - Cannot be /login to avoid redirect loops
 *
 * @example Valid URLs:
 * - "/themes/123/images#overview" ✅
 * - "/select-brand?tab=all" ✅
 * - "/internal/users" ✅
 *
 * @example Blocked URLs:
 * - "http://evil.com" ❌ (external)
 * - "//evil.com" ❌ (protocol-relative)
 * - "javascript:alert('xss')" ❌ (JS execution)
 * - "data:text/html,<script>..." ❌ (data URI)
 * - "/login" ❌ (redirect loop)
 */
const isValidInternalUrl = (url: string): boolean => {
  // Must be a string
  if (typeof url !== "string" || url.length === 0) return false;

  // Must start with /
  if (!url.startsWith("/")) return false;

  // Cannot be protocol-relative
  if (url.startsWith("//")) return false;

  // Cannot contain : before first / (prevents javascript:, data:, http:, etc.)
  const firstSlash = url.indexOf("/", 1); // Start from index 1 since we know it starts with /
  const firstColon = url.indexOf(":");
  if (firstColon !== -1 && (firstSlash === -1 || firstColon < firstSlash)) {
    return false;
  }

  // Don't redirect to /login (avoid loops)
  if (url === "/login" || url.startsWith("/login?") || url.startsWith("/login#")) {
    return false;
  }

  return true;
};

/**
 * Get the full current URL including pathname, search params, and hash.
 * Used by auth utilities before redirecting to login.
 *
 * @example
 * // If current URL is /themes/123/images?tab=all#overview
 * getFullCurrentUrl() // Returns "/themes/123/images?tab=all#overview"
 */
export const getFullCurrentUrl = (): string => window.location.pathname + window.location.search + window.location.hash;

/**
 * Save the redirect URL to localStorage.
 * Called by Auth.logout('preserve-redirect') before redirecting to login.
 *
 * @param url - The URL to redirect to after login (or null to clear)
 */
export const setRedirectUrl = (url: string | null): void => {
  if (url) {
    // Validate URL before storing (defense in depth)
    if (!isValidInternalUrl(url)) {
      console.warn("[redirect-url] Attempted to store invalid redirect URL:", url);
      return;
    }
    localStorage.setItem(REDIRECT_URL_STORAGE_KEY, url);
  } else {
    localStorage.removeItem(REDIRECT_URL_STORAGE_KEY);
  }
};

/**
 * Get the stored redirect URL from localStorage.
 * Called by login pages on successful authentication.
 *
 * @returns The stored redirect URL, or null if none exists
 */
export const getRedirectUrl = (): string | null => {
  const stored = localStorage.getItem(REDIRECT_URL_STORAGE_KEY);
  if (!stored) return null;

  // Validate URL when reading (defense in depth)
  if (!isValidInternalUrl(stored)) {
    console.warn("[redirect-url] Invalid redirect URL found in storage, clearing:", stored);
    localStorage.removeItem(REDIRECT_URL_STORAGE_KEY);
    return null;
  }

  return stored;
};

/**
 * Clear the stored redirect URL from localStorage.
 * Called after successful redirect to prevent stale redirects.
 */
export const clearRedirectUrl = (): void => {
  localStorage.removeItem(REDIRECT_URL_STORAGE_KEY);
};

/**
 * Result of redirect URL resolution
 */
export interface RedirectResolutionResult {
  url: string;
  hadInvalidUrl: boolean;
  invalidUrlSource?: "storage" | "query";
}

/**
 * Resolve the post-login redirect URL with proper priority and cleanup.
 * Returns both the resolved URL and whether any invalid URLs were detected.
 *
 * Priority order:
 * 1. Stored redirect URL (from Auth.logout('preserve-redirect'))
 * 2. Query parameter redirect URL (from ?redirect_url=...)
 * 3. Default fallback URL (app-specific default)
 *
 * Automatically clears stored redirect URL after reading to prevent stale redirects.
 *
 * @param queryRedirectUrl - Optional redirect URL from query params
 * @param defaultUrl - Fallback URL if no redirect is found
 * @returns Resolution result with URL and validation info
 *
 * @example
 * // In web app login
 * const result = resolvePostLoginRedirect(searchParams.get('redirect_url'), '/select-brand');
 * if (result.hadInvalidUrl) {
 *   toast.error("Invalid navigation link detected. Redirecting to home.");
 * }
 * navigate(result.url);
 *
 * @example
 * // In internal/media app login
 * const result = resolvePostLoginRedirect(null, '/');
 * navigate(result.url);
 */
export const resolvePostLoginRedirect = (
  queryRedirectUrl?: string | null,
  defaultUrl: string = "/"
): RedirectResolutionResult => {
  const storedRedirectUrl = getRedirectUrl();
  let hadInvalidUrl = false;
  let invalidUrlSource: "storage" | "query" | undefined;

  // Priority: stored > query param > default
  let finalUrl = defaultUrl;

  if (storedRedirectUrl) {
    finalUrl = storedRedirectUrl;
  } else if (queryRedirectUrl) {
    if (isValidInternalUrl(queryRedirectUrl)) {
      finalUrl = queryRedirectUrl;
    } else {
      hadInvalidUrl = true;
      invalidUrlSource = "query";
      console.warn("[redirect-url] Invalid query param redirect URL blocked:", queryRedirectUrl);
    }
  }

  // Clear stored redirect to prevent stale redirects
  clearRedirectUrl();

  return { url: finalUrl, hadInvalidUrl, invalidUrlSource };
};
